import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBars, faTimes, faUser, faSignOutAlt, faUserCircle,
  faHome, faHandshake, faGlassCheers, 
  faCog, faEnvelope, faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { useAppContext } from '../../context/AppContext';
import { logger } from '../../utils/logger';

// Type for Link props from react-router
interface LinkWrapperProps {
  to: string;
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
}

// Create a wrapper for Link to handle props properly with styled-components
const LinkWrapper = ({ className, children, to, onClick }: LinkWrapperProps) => (
  <Link 
    to={to}
    className={className} 
    onClick={onClick}
  >
    {children}
  </Link>
);

// Interface for user menu props
interface UserMenuProps {
  $userMenuOpen?: boolean;
}

// Interface for navbar items
interface NavbarProps {
  $scrolled?: boolean;
  $menuOpen?: boolean;
}

// Define menu sections for organization
interface MenuSection {
  title: string;
  links: {
    name: string;
    to: string;
    icon: any;
  }[];
}

const Navbar = (): React.ReactElement => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  // Removed unused state variable
  const [, setIsMobile] = useState(false);  const location = useLocation();
  const { currentUser, userProfile, signOut } = useAuth();
  const { user } = useAppContext();
  const userMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Handle scroll effect and check for mobile device
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    handleResize();
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    // Clean up the event listeners on component unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Toggle mobile menu
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
    // Add no-scroll class to body when menu is open
    document.body.style.overflow = !menuOpen ? 'hidden' : 'auto';
  };
  
  // Close the menu
  const closeMenu = () => {
    setMenuOpen(false);
    document.body.style.overflow = 'auto';
  };

  // Toggle user dropdown menu
  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut();
      logger.info('👋 User signed out from navbar');
    } catch (error) {
      logger.error('❌ Error signing out from navbar', error);
    }
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuOpen &&
        mobileMenuRef.current && 
        !mobileMenuRef.current.contains(event.target as Node) && 
        !(event.target as Element).closest('[data-testid="menu-button"]')
      ) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  // Close the menu when a link is clicked or when route changes
  useEffect(() => {
    setMenuOpen(false);
    setUserMenuOpen(false);
    document.body.style.overflow = 'auto';
  }, [location]);

  // Organize menu items into sections
  const menuSections: MenuSection[] = [
    {
      title: "Main",
      links: [
        { name: 'Home', to: '/', icon: faHome },
        { name: 'Events', to: '/events', icon: faCalendarAlt },
        { name: 'Experience', to: '/experience', icon: faGlassCheers },
      ]
    },
    {
      title: "Info",
      links: [
        { name: 'Consent & Guidelines', to: '/consent', icon: faHandshake },
        // TODO: Temporarily hiding Feedback navigation item until functionality is fully implemented
        // Uncomment the line below when ready to restore the Feedback feature
        // { name: 'Feedback & Rating System', to: '/feedback', icon: faCommentAlt },
        { name: 'Contact', to: '/contact', icon: faEnvelope }
      ]
    }
  ];

  // Add Admin section if user has admin rights
  if (user && user.isAdmin) {
    menuSections.push({
      title: "Admin",
      links: [
        { name: 'Admin Dashboard', to: '/admin', icon: faCog }
      ]
    });
  }
  
  return (
    <NavContainer $scrolled={scrolled}>
      <NavContent>        <Link to="/" style={{ textDecoration: 'none' }}>
          <Logo>
            <LogoImage src="/images/logo.png" alt="BodySharing Logo" />
            <h1>BODYSHARING</h1>
          </Logo>
        </Link>
        <MenuButton onClick={toggleMenu} data-testid="menu-button">
          <FontAwesomeIcon icon={menuOpen ? faTimes : faBars} />
        </MenuButton>
        
        {/* Desktop Navigation */}
        <DesktopNav>
          <NavList>
            {menuSections.flatMap(section => section.links).map((link, index) => (
              <NavItem key={index}>
                <NavMenuLink
                  to={link.to}
                  className={location.pathname === link.to ? 'active' : ''}
                >
                  {link.name}
                </NavMenuLink>
              </NavItem>
            ))}
            
            {/* Authentication links */}
            {!currentUser ? (
              <NavItem>
                <NavMenuLink
                  to="/sign-in"
                  className={location.pathname === '/sign-in' ? 'active' : ''}
                >
                  Sign In
                </NavMenuLink>
              </NavItem>
            ) : (
              <NavItem className="user-menu-container">
                <UserMenuTrigger onClick={toggleUserMenu}>
                  {currentUser.photoURL ? (
                    <UserAvatar 
                      src={currentUser.photoURL} 
                      alt="Profile" 
                    />
                  ) : (
                    <FontAwesomeIcon icon={faUserCircle} />
                  )}                  <Username>
                    {userProfile?.username || 'User'}
                  </Username>
                </UserMenuTrigger>
                {userMenuOpen && (
                  <UserDropdownMenu ref={userMenuRef} $userMenuOpen={userMenuOpen}>
                    <DropdownItem to="/my-profile">
                      <DropdownIcon>
                        <FontAwesomeIcon icon={faUser} />
                      </DropdownIcon>
                      My Profile
                    </DropdownItem>
                    <DropdownDivider />
                    <DropdownButton onClick={handleSignOut}>
                      <DropdownIcon>
                        <FontAwesomeIcon icon={faSignOutAlt} />
                      </DropdownIcon>
                      Sign Out
                    </DropdownButton>
                  </UserDropdownMenu>
                )}
              </NavItem>
            )}
          </NavList>
        </DesktopNav>
            {/* Mobile Menu */}
        {menuOpen && (
          <>
            <MenuOverlay onClick={closeMenu} />
            <MobileMenuContainer ref={mobileMenuRef} $menuOpen={menuOpen}>
              {/* User Profile Section (if logged in) */}
              {currentUser && (
                <MobileUserSection>
                  <UserProfileContainer>
                    {currentUser.photoURL ? (
                      <MobileUserAvatar 
                        src={currentUser.photoURL} 
                        alt="Profile" 
                      />
                    ) : (
                      <UserIconContainer>
                        <FontAwesomeIcon icon={faUserCircle} size="2x" />
                      </UserIconContainer>
                    )}                    <MobileUsername>
                      {userProfile?.username || 'User'}
                    </MobileUsername>
                  </UserProfileContainer>
                  
                  <MobileUserLinks>
                    <MobileNavLink to="/my-profile" onClick={closeMenu}>
                      <MobileLinkIcon>
                        <FontAwesomeIcon icon={faUser} />
                      </MobileLinkIcon>
                      My Profile
                    </MobileNavLink>
                    <MobileMenuDivider />
                    <MobileButton onClick={() => { handleSignOut(); closeMenu(); }}>
                      <MobileLinkIcon>
                        <FontAwesomeIcon icon={faSignOutAlt} />
                      </MobileLinkIcon>
                      Sign Out
                    </MobileButton>
                  </MobileUserLinks>
                </MobileUserSection>
              )}
                {/* Navigation Sections */}
              {menuSections.map((section, index) => (
                <MenuSectionContainer key={index}>
                  <SectionTitle>{section.title}</SectionTitle>
                  <MobileNavList>
                    {section.links.map((link, linkIndex) => (
                      <MobileNavItem key={linkIndex}>
                        <MobileNavLink 
                          to={link.to} 
                          onClick={closeMenu}
                          className={location.pathname === link.to ? 'active' : ''}
                        >
                          <MobileLinkIcon>
                            <FontAwesomeIcon icon={link.icon} />
                          </MobileLinkIcon>
                          {link.name}
                        </MobileNavLink>
                      </MobileNavItem>
                    ))}
                  </MobileNavList>
                </MenuSectionContainer>
              ))}
              
              {/* Sign In Link for non-authenticated users */}
              {!currentUser && (
                <MenuSectionContainer>
                  <SectionTitle>Account</SectionTitle>
                  <MobileNavList>
                    <MobileNavItem>
                      <MobileNavLink 
                        to="/sign-in" 
                        onClick={closeMenu}
                        className={location.pathname === '/sign-in' ? 'active' : ''}
                      >
                        <MobileLinkIcon>
                          <FontAwesomeIcon icon={faSignOutAlt} />
                        </MobileLinkIcon>
                        Sign In
                      </MobileNavLink>
                    </MobileNavItem>
                  </MobileNavList>                </MenuSectionContainer>
              )}
            </MobileMenuContainer>
          </>
        )}
      </NavContent>
    </NavContainer>
  );
};

// Styled Components
const NavContainer = styled.header<NavbarProps>`
  position: fixed;
  top: 0;
  width: 100%;
  height: 70px;
  z-index: 1000;
  background: ${({ $scrolled }: NavbarProps) => ($scrolled ? 'rgba(20, 20, 20, 0.95)' : 'rgba(20, 20, 20, 0.8)')};
  backdrop-filter: blur(10px);
  box-shadow: ${({ $scrolled }: NavbarProps) => ($scrolled ? '0 2px 10px rgba(0, 0, 0, 0.3)' : 'none')};
  transition: all 0.3s ease;
`;

const NavContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  padding: 0 5%;
`;

const LogoImage = styled.img`
  height: 32px;
  width: auto;
  margin-right: 10px;
  border-radius: 8px; /* Added rounded corners */
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  
  h1 {
    font-size: 1.5rem;
    margin: 0;
    color: var(--text-color, #ffffff);
  }
  
  @media (max-width: 480px) {
    h1 {
      font-size: 1.2rem;
    }
  }
`;

const MenuButton = styled.button`
  display: none;
  background: transparent;
  border: none;
  color: var(--text-color, #ffffff);
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 1001;
  
  @media (max-width: 768px) {
    display: flex;
    margin-left: 20px;
  }
`;

const DesktopNav = styled.div`
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  align-items: center;
`;

const NavItem = styled.li`
  margin: 0 1rem;
  display: flex;
  align-items: center;
`;

const NavMenuLink = styled(LinkWrapper)`
  color: var(--text-color, #ffffff);
  text-decoration: none;
  padding: 0.5rem 0;
  position: relative;
  font-weight: 500;
  display: flex;
  align-items: center;
  height: 100%; /* Add consistent height */
  
  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    background: var(--primary-color, #00bfff);
    bottom: 0;
    left: 0;
    transition: width 0.3s ease;
  }
  
  &:hover:after, 
  &.active:after {
    width: 100%;
  }
  
  &.active {
    color: var(--primary-color-light, #66d9ff);
  }
`;

const UserMenuTrigger = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  height: 100%; /* Add a consistent height */

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const UserAvatar = styled.img`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--accent-color, #64b5f6);
  background-color: #2c2c2c;
`;

const Username = styled.span`
  margin-left: 0.5rem;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UserDropdownMenu = styled.div<UserMenuProps>`
  position: absolute;
  top: 100%;
  right: 0;
  background: rgba(26, 26, 26, 0.95);
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  min-width: 220px;
  z-index: 1001;
  overflow: hidden;
  margin-top: 0.75rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const DropdownItem = styled(LinkWrapper)`
  display: flex;
  align-items: center;
  padding: 0 1.25rem;
  color: var(--text-color, #ffffff);
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;
  height: 34px;
  width: 100%;
  font-size: 1.1rem;
  font-weight: 500;

  &:hover {
    background: linear-gradient(135deg, var(--primary-color, rgba(156, 39, 176, 0.3)), var(--secondary-color, rgba(233, 30, 99, 0.3)));
    transform: translateY(-2px);
  }
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  padding: 0 1.25rem;
  color: var(--text-color, #ffffff);
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
  font-size: 1.1rem;
  font-weight: 500;
  height: 34px;

  &:hover {
    background: linear-gradient(135deg, var(--primary-color, rgba(156, 39, 176, 0.3)), var(--secondary-color, rgba(233, 30, 99, 0.3)));
    transform: translateY(-2px);
  }
`;

const DropdownIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  width: 1.2rem;
  min-width: 1.2rem;
  height: 1.2rem;
  color: var(--accent-color, #64b5f6);
`;

const DropdownDivider = styled.div`
  height: 1px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.05));
  margin: 0.5rem 0;
`;

// Mobile menu styled components
const MenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  z-index: 998;
  transition: opacity 0.3s ease;
`;

const MobileMenuContainer = styled.div<NavbarProps>`
  position: fixed;
  top: 0;
  right: 0;
  width: 320px;
  height: 100vh;
  background: rgba(22, 22, 22, 0.95);
  z-index: 999;
  overflow-y: auto;
  transform: translateX(${({ $menuOpen }) => ($menuOpen ? '0' : '100%')});
  transition: transform 0.3s ease-in-out;
  box-shadow: -5px 0 15px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  backdrop-filter: blur(10px);
  padding-top: 20px;
`;



const MobileUserSection = styled.div`
  padding: 0 20px 20px;
  margin-bottom: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const UserProfileContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
`;

const MobileUserAvatar = styled.img`
  width: 45px;
  height: 45px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--accent-color, #64b5f6);
`;

const UserIconContainer = styled.div`
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--accent-color, #64b5f6);
`;

const MobileUsername = styled.span`
  margin-left: 10px;
  font-size: 1.2rem;
  font-weight: 500;
`;

const MobileUserLinks = styled.div`
  display: flex;
  flex-direction: column;
`;

const MenuSectionContainer = styled.div`
  margin-bottom: 8px;
  padding: 0 20px;
  
  &:first-of-type {
    margin-top: 30px;
  }
`;

const SectionTitle = styled.h3`
  margin: 0;
  padding: 0 10px;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 6px;
  margin-top: 6px;
  font-weight: 500;
`;

const MobileNavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const MobileNavItem = styled.li`
  margin-bottom: 2px;
`;

const MobileNavLink = styled(LinkWrapper)`
  display: flex;
  align-items: center;
  padding: 8px 8px;
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  font-size: 1rem;
  font-weight: 500;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
    transform: translateX(3px);
  }
  
  &.active {
    background: linear-gradient(135deg, var(--primary-color, rgba(156, 39, 176, 0.3)), var(--secondary-color, rgba(233, 30, 99, 0.3)));
    color: white;
  }
`;

const MobileButton = styled.button`
  display: flex;
  align-items: center;
  padding: 12px 10px;
  color: rgba(255, 255, 255, 0.85);
  background: none;
  border: none;
  font-size: 1.1rem;
  font-weight: 500;
  border-radius: 6px;
  transition: all 0.2s ease;
  width: 100%;
  text-align: left;
  cursor: pointer;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
    transform: translateX(3px);
  }
`;

const MobileLinkIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  width: 20px;
  color: var(--accent-color, #64b5f6);
`;

const MobileMenuDivider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 8px 0;
`;

export default Navbar;
